import { canonicalThumbSource, encodeThumbSource } from "./static-thumb-contract.ts";

/**
 * 小图 URL 的唯一实现，服务端与管端共用。静态映射缺失时由读图入口回原图。
 * 服务端从 media.ts 再导出，调用方照旧 `import { thumbUrl } from "../media.ts"`。
 */
/**
 * 小图档位（按显示用途命名，**调用方只引这里的名字，不写数字**）。值是输出宽度（像素），
 * 高度按原图比例。CDN 只缩不放：原图比档位小时原样尺寸输出。
 *
 * 数字按客户端设计分辨率 402 宽、fitWidth、最宽手机 1440px（×3.6）量出的各图位最大像素定
 *（fly-game-app 2026-10-22 逐个图位量的），平板没算。
 *
 * 档位与 static-thumb-contract.ts 的上传规格一致。改规格需同步上传与补图，并增加资源 BUILDER_VERSION。
 */
export const THUMB_TIERS = {
  /** 物品说明、成就这类小图标（最大显示约 61–150px） */
  small: 160,
  /** 头像、头像框、天赋图标、卡包封面（最大显示约 140–330px；个人页大头像仍用原图） */
  icon: 320,
  /** 卡面缩略、选关卡片、商城格子、拼图列表图（最大显示约 400–640px） */
  card: 640,
  /** 建造页拼图大图（板边长约 1142px） */
  board: 1152,
} as const;
export type ThumbTier = keyof typeof THUMB_TIERS;

/**
 * 原图域名 → 对应环境的公开静态小图入口。文件仍在原有图床。
 */
const STATIC_THUMB_ORIGINS: Record<string, string> = {
  "pbtest.buyacard.cc": "https://aeroplane.pwtk-dev.work",
};

/**
 * 原图 URL → 静态小图入口，纯字符串变换，不给业务接口增加 SQL。
 * 入口缓存原图到静态 WebP/PNG 的跳转；旧图未补齐时直接回原图。
 * 外链、带查询参数的图和已处理的 URL 原样返回。
 */
export function thumbUrl(url: string, tier: ThumbTier): string {
  if (!url) return url;
  let u: URL;
  try { u = new URL(url); } catch { return url; }
  // 生产静态入口尚未发布；test 历史配置也会引用 pp，不能把它们送到不存在的生产新路由。
  if (u.protocol === "https:" && u.host === "resource.bingo84.com" && u.pathname.startsWith("/pp/") && !u.username && !u.password) {
    return `${u.origin}/cdn-cgi/image/width=${THUMB_TIERS[tier]},format=webp${u.pathname}${u.search}`;
  }
  const origin = STATIC_THUMB_ORIGINS[u.host];
  if (!origin || !canonicalThumbSource(url)) return url;
  return `${origin}/media/thumb/v1/${THUMB_TIERS[tier]}/${encodeThumbSource(u.href)}`;
}
